import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import {
  useMediaQuery,
  Card,
  Box,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import { searchCompanies } from '../../services/advisor_services';
import { useAccount } from '../../contexts/Account';
import AdvisorSearchCompanies from './SearchCompanies';
import SceneContentStateSelector from '../../components/SceneContentStateSelector';
import {
  EndpointResponseObjectName,
  EndpointParameterFilters,
} from '../../services/utils/types';
import { isNil } from '../../services/utils';
import CompaniesFilter from './CompaniesFilter';
import eventBus, { eventBusValues } from '../../eventBus';
import InvitationsBoard from '../../components/Advisor/InvitationsBoard';
import CompanyTopTilesContainer from '../../components/Advisor/TopBar/TopTiles';

const useStyles = (isSM) => ({
  container: {
    padding: `20px ${isSM ? '10px' : '30px'}`,
    marginTop: '24px',
    width: '100%',
  },
  chip: {
    backgroundColor: '#F5F7FA',
    color: '#000',
  },
});

function isPeerPage(location) {
  if (location.pathname.includes('/board-directory')) {
    return false;
  }
  return true;
}

function AdvisoryBoards() {
  const location = useLocation();
  const peerPage = isPeerPage(location);

  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { asLoggedUserDetails } = useAccount();
  const [doNotFetch, setDoNotFetch] = useState(false);
  const [hideInvitationsTile, setHideInvitationsTile] = useState(false);
  const classes = useStyles(isSM);
  const companyId = location?.state?.companyId;

  function getQueryParam() {
    if (location.search) {
      const queryParams = new URLSearchParams(location.search);
      return queryParams.get('keyword') || '';
    }
    return '';
  }

  const boardMeetingsPage = location.pathname === '/board-meetings';

  const [queryParameters, setQueryParameters] = useState({
    ADVISOR_ID: asLoggedUserDetails.userId,
    SEARCH_STRING: getQueryParam(),
    LIMIT: EndpointParameterFilters.COMPANIES_FETCH_LIMIT,
    OFFSET: 0,
    UPCOMING_BOARD_MEETINGS_ONLY: boardMeetingsPage,
    PEER_FILTER: peerPage ? 'P2P' : 'Company',
  });

  function handleSearch(props) {
    const { keywords, skills, industries } = props;
    const searchString = {
      ADVISOR_ID: asLoggedUserDetails.userId,
      LIMIT: EndpointParameterFilters.COMPANIES_FETCH_LIMIT,
      OFFSET: 0,
      PEER_FILTER: peerPage ? 'P2P' : 'Company',
    };

    if (keywords && keywords.length > 0) {
      searchString.SEARCH_STRING = keywords;
    }
    if (!isNil(skills) && skills[0]) {
      searchString.SKILLS_TAGS_IDS = skills.map((skill) => skill.id);
    }
    if (!isNil(industries) && industries[0]) {
      searchString.INDUSTRY_TAGS_IDS = industries.map(
        (industry) => industry.id
      );
    }

    setDoNotFetch(false);
    setQueryParameters(searchString);
  }

  useEffect(() => {
    setQueryParameters((prevParams) => ({
      ...prevParams,
      SEARCH_STRING: getQueryParam(),
    }));
  }, [location.search]);

  useEffect(() => {
    if (!companyId) return;
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      companyId: companyId,
    });
  }, [companyId]);

  useEffect(() => {
    setQueryParameters({
      ADVISOR_ID: asLoggedUserDetails.userId,
      SEARCH_STRING: getQueryParam(),
      LIMIT: EndpointParameterFilters.COMPANIES_FETCH_LIMIT,
      OFFSET: 0,
      UPCOMING_BOARD_MEETINGS_ONLY: boardMeetingsPage,
      PEER_FILTER: peerPage ? 'P2P' : 'Company',
    });

    setDoNotFetch(false);
  }, [location.pathname, peerPage]);

  return (
    <div style={classes.container}>
      <Box
        sx={{
          bgcolor: 'white',
          zIndex: 0,
          borderRadius: '15px',
          mb: '30px',
        }}
      >
        <CompanyTopTilesContainer
          circleFirstColor="rgba(215, 241, 253, 1)"
          circleSecondColor="rgba(219, 247, 235, 1)"
          title="Join Exciting Boards, Share Your Expertise"
          subtitle="Our platform makes it easy and rewarding to be on boards built by 100s of organizations on AdvisoryCloud. Get monthly updates and enjoy a variety of perks for board members."
          circleAlternative
          compactView
          zIndex={1}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: isSM ? '10px' : '20px',
              mt: '20px',
            }}
          >
            <Chip label="Emerging Startups" sx={classes.chip} />
            <Chip label="Private Companies" sx={classes.chip} />
            <Chip label="International Business" sx={classes.chip} />
            <Chip label="Non-Profits" sx={classes.chip} />
            <Chip label="Corporate Teams" sx={classes.chip} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px',
              my: '20px',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              id="oppsGetRecommendations"
              className="headerButton"
              sx={{
                width: '218px',
                borderRadius: '40px',
                backgroundColor: '#3171F6',
                textTransform: 'none',
                height: '45px',
                margin: '5px',
                fontStyle: 'normal',
                fontWeight: 700,
                fontSize: '15px',
                lineHeight: '24px',
                letterSpacing: '-0.43px',
                color: '#FFFFFF',
                padding: '5px',
                '&:hover': {
                  backgroundColor: '#3171F6',
                },
              }}
            >
              Get Recommendations
            </Button>
          </Box>
        </CompanyTopTilesContainer>
      </Box>
      <Card
        sx={{
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: 'none',
          maxWidth: '100%',
          border: '1px solid #E0E0E0',
          display: hideInvitationsTile ? 'none' : 'block',
        }}
      >
        <InvitationsBoard setHideComponent={setHideInvitationsTile} />
      </Card>
      {!peerPage && <CompaniesFilter onSearchButtonClicked={handleSearch} />}

      <SceneContentStateSelector
        helmetTitle="Board Directory"
        endPointAppSyncName={EndpointResponseObjectName.SEARCH_COMPANIES}
        endPointCall={searchCompanies}
        endPointCallParameters={queryParameters}
        ContentTag={AdvisorSearchCompanies}
        doNotShowEmptyStateScene
        doNotFetch={doNotFetch}
        isBoardSekeleton
        noMaxWidth
        setFullScreen
        contentProps={{
          emptySearchParams:
            !queryParameters.SEARCH_STRING &&
            (!queryParameters.SKILLS_TAGS_IDS ||
              queryParameters.SKILLS_TAGS_IDS?.length === 0) &&
            (!queryParameters.INDUSTRY_TAGS_IDS ||
              queryParameters.INDUSTRY_TAGS_IDS?.length === 0),
        }}
      />
    </div>
  );
}

export default AdvisoryBoards;
